import { h, ComponentChild, ComponentType } from 'preact';
import { useTranslation } from 'react-i18next';
import trim from 'src/common/helpers/trim';
// import { useEffect, useState } from 'preact/hooks';
import copyToClipboard from 'src/common/helpers/copyToClipboard';
import cn from 'classnames';

import 'src/common/templates/styling/default.layout/fair/fair.scss';
import 'src/common/templates/styling/default.layout/modals/ResultModal.scss';

type ModalProps = {
    isMobile: boolean;
    children: ComponentChild;
    withHash: boolean;
    Logo: ComponentType;
    betData: {
        amount?: number;
        coefficient: number;
        currency: string;
        gameId: string;
        game: {
            clientSeed?: string;
            nonce: number;
            serverSeed: string;
            hash: string;
            rtp: number;
            salt?: string;
            encryptedHash?: string;
        };
        payout: number;
        playerId: string;
        playerName: string;
        updatedAt: string;
        result: 'won' | 'lost';
        id: string;
        player: {
            id: string;
            nickname: string;
        };
        locale: string | null;
        betPayoutConverted?: string;
        betAmountConverted?: string;
        freebets: boolean;
    };
    fair?: boolean;
};

function ResultModal({
    isMobile,
    children,
    betData,
    withHash,
    Logo,
    fair,
}: ModalProps) {
    if (!betData) return null;

    const { t } = useTranslation();

    const {
        amount,
        coefficient,
        currency,
        gameId,
        game,
        payout,
        // playerId,
        playerName,
        updatedAt,
        id,
        player: { id: playerId, nickname },
        locale,
        betPayoutConverted,
        betAmountConverted,
        freebets,
    } = betData;

    const newDate = new Date(updatedAt);
    const lang = !locale || locale.length > 2 ? 'en' : locale;
    const formatted = new Intl.DateTimeFormat(lang || 'en', {
        // hour12: true,
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: 'numeric',
    }).format(newDate);

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.append(
        'local',
        String(process.env.NODE_ENV === 'development'),
    );
    searchParams.append('game', String(gameId));
    searchParams.append('data', JSON.stringify(game));

    const fairParams = new URLSearchParams(searchParams);
    const fairUrl =
        process.env.NODE_ENV === 'development'
            ? `//localhost:3000/fair?${fairParams.toString()}`
            : `//landing.dev.turbogames.work/fair?${fairParams.toString()}`;

    return (
        <div className="stats-modal">
            <div className="stats-modal__head">
                <Logo />
                <div className="stats-modal__head-id">
                    {trim(id)}
                    <i
                        className="iconFont iconFont-copy"
                        onClick={() => copyToClipboard(id)}
                    />
                </div>
            </div>
            <div className="stats-modal__row">
                <div className="title">{t('COMMON.PLAYER_ID')}:</div>
                <div className="text">{playerId}</div>
            </div>
            <div className="stats-modal__row">
                <div className="title">{t('COMMON.NICKNAME')}:</div>
                <div className="text">{nickname}</div>
            </div>
            <div className="stats-modal__row">
                <div className="title">{t('COMMON.DATE')}:</div>
                {locale === 'tr106' ? (
                    <div className="text">
                        {formatted.replace('AM', 'Ö.Ö.').replace('PM', 'Ö.S.')}
                    </div>
                ) : (
                    <div className="text">{formatted}</div>
                )}
            </div>
            <div className="stats-modal__row">
                <div className="stats-modal__col">
                    <div
                        className={cn('stats-modal__block-flat', {
                            _yellow: freebets,
                        })}
                    >
                        <div className="stats-modal__block-label">
                            {freebets ? 'FREE BET' : t('COMMON.BET_AMOUNT')}
                        </div>
                        <div className="stats-modal__block-value">
                            {betAmountConverted
                                ? betAmountConverted
                                : `${amount} ${currency}`}
                        </div>
                    </div>
                </div>
                <div className="stats-modal__col">
                    <div className="stats-modal__block-flat">
                        <div className="stats-modal__block-label">
                            {t('COMMON.MULTIPLIER')}
                        </div>
                        <div className="stats-modal__block-value">
                            х{coefficient}
                        </div>
                    </div>
                </div>
                <div className="stats-modal__col">
                    <div className="stats-modal__block-flat">
                        <div className="stats-modal__block-label">
                            {t('COMMON.PAYOUT')}
                        </div>
                        <div className="stats-modal__block-value">
                            {betPayoutConverted
                                ? betPayoutConverted
                                : `${payout} ${currency}`}
                        </div>
                    </div>
                </div>
            </div>
            <div className="stats-modal__interface">{children}</div>
            {game && (
                <>
                    {(!!game.clientSeed || !!game.nonce) && (
                        <div className="stats-modal__block-group">
                            {game.clientSeed && (
                                <div className="stats-modal__block">
                                    <div className="stats-modal__block--inner">
                                        <div className="stats-modal__block-label">
                                            Client Seed
                                        </div>
                                        <div className="stats-modal__block-value">
                                            {game.clientSeed}
                                        </div>
                                        <i
                                            className="iconFont iconFont-copy"
                                            onClick={() =>
                                                copyToClipboard(game.clientSeed)
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                            {game.nonce && (
                                <div className="stats-modal__block">
                                    <div className="stats-modal__block--inner">
                                        <div className="stats-modal__block-label">
                                            Nonce
                                        </div>
                                        <div className="stats-modal__block-value">
                                            {game.nonce}
                                        </div>
                                        <i
                                            className="iconFont iconFont-copy"
                                            onClick={() =>
                                                copyToClipboard(game.nonce)
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {game.serverSeed && (
                        <div className="stats-modal__block">
                            <div className="stats-modal__block--inner">
                                <div className="stats-modal__block-label">
                                    Server seed
                                </div>
                                <div className="stats-modal__block-value">
                                    {game.serverSeed}
                                </div>
                                <i
                                    className="iconFont iconFont-copy"
                                    onClick={() =>
                                        copyToClipboard(game.serverSeed)
                                    }
                                />
                            </div>
                        </div>
                    )}
                    {withHash && game.hash && (
                        <div className="stats-modal__block">
                            <div className="stats-modal__block--inner">
                                <div className="stats-modal__block-label">
                                    Hash
                                </div>
                                <div className="stats-modal__block-value">
                                    {game.hash}
                                </div>
                                <i
                                    className="iconFont iconFont-copy"
                                    onClick={() => copyToClipboard(game.hash)}
                                />
                            </div>
                        </div>
                    )}
                    {game.salt && (
                        <div className="stats-modal__block">
                            <div className="stats-modal__block--inner">
                                <div className="stats-modal__block-label">
                                    Salt
                                </div>
                                <div className="stats-modal__block-value">
                                    {game.salt}
                                </div>
                                <i
                                    className="iconFont iconFont-copy"
                                    onClick={() =>
                                        copyToClipboard(game.salt)
                                    }
                                />
                            </div>
                        </div>
                    )}
                    {game.encryptedHash && (
                        <div className="stats-modal__block">
                            <div className="stats-modal__block--inner">
                                <div className="stats-modal__block-label">
                                    Encrypted Hash
                                </div>
                                <div className="stats-modal__block-value">
                                    {game.encryptedHash}
                                </div>
                                <i
                                    className="iconFont iconFont-copy"
                                    onClick={() =>
                                        copyToClipboard(game.encryptedHash)
                                    }
                                />
                            </div>
                        </div>
                    )}
                    {/* <div className="stats-modal__block">
                        <div
                            className="stats-modal__block-button"
                            onClick={verifyClick}
                        >
                            Verify
                        </div>
                    </div> */}
                </>
            )}

            {fair && (
                <a className="fair-button" href={fairUrl}>
                    Probable fair
                </a>
            )}
        </div>
    );
}

export default ResultModal;
